import useMethodStore, { useMethodActions } from "@/store/MethodStore";
import { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { marked } from "marked";
import { FaX } from "react-icons/fa6";
import LoadingDots from "../LoadingDots";
import axios from "axios";

type Message = {
  role: "user" | "model";
  text: string;
  timestamp: number;
};

const questionSuggestions = [
  "Are you available to hire?",
  "How much time do you take to code a website?",
  "What are your skills?",
]

const AskMe = () => {
  const { toggleAskMe } = useMethodActions();
  const askMeState = useMethodStore(state => state.askMeState)

  const [messages, setMessages] = useState<Message[]>([
    {
      role: "model",
      text: "Hi! I'm Dhvanit's AI persona. Ask me anything about him or his work. I'll be happy to assist you.",
      timestamp: Date.now()
    }
  ]);
  const [newMessage, setNewMessage] = useState("");
  const [gettingResponse, setGettingResponse] = useState(false);

  const renderer = new marked.Renderer();

  renderer.code = ({ text, lang }) => {
    const languageClass = lang ? `language-${lang}` : "";
    return `<pre class="p-1.5 overflow-auto"><code class="${languageClass} text-slate-700 whitespace-pre-wrap break-words text-xs">${text}</code></pre>`;
  };

  renderer.link = ({ href, text }) =>
    `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-500 dark:text-blue-500 underline underline-offset-2 transition duration-150 ease-in-out hover:text-blue-600 dark:hover:text-blue-600 hover:underline">${text}</a>`;

  marked.setOptions({ renderer });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to the bottom of the messages container
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "c" && e.ctrlKey) toggleAskMe();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [toggleAskMe]);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Add a new message to the chat
  const addMessage = useCallback((text: string, role: "user" | "model") => {
    const newMessage: Message = {
      text,
      timestamp: Date.now(),
      role,
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
  }, []);

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || gettingResponse) return;

    try {
      // Add user's message to the chat
      addMessage(newMessage, "user");
      setNewMessage("");

      // Fetch AI response
      setGettingResponse(true);

      const response = await axios.post(`${import.meta.env.VITE_SERVER_URI}/ai/generate`,
        {
          prompt: newMessage,
        });

      if (!response.data || !response.data.text) {
        toast.error("Failed to send message. Please try again.")
        return
      }

      // Add AI's response to the chat
      addMessage(response.data.text, "model");

    } catch (error) {
      console.error(error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setGettingResponse(false);
    }
  };

  return (
    <div
      className={`flex flex-col fixed ${askMeState ? "right-0" : "-right-full"} backdrop-blur-md transition-all border-l-1 border-zinc-4 dark:border-zinc-800 duration-300 top-0 md:max-w-[26rem] w-full h-[calc(100vh-4.2rem)] lg:h-screen bg-zinc-100/80 dark:bg-zinc-900/80`}
    >
      {/* Chat Header */}
      <div className="p-4 border-b-1 flex justify-between items-center bg-zinc-100/60 dark:bg-zinc-900/60 shadow-sm">
        <h1 className="text-2xl font-bold text-zinc-800 dark:text-zinc-200">Ask Dhvanit</h1>
        <button
          className="text-zinc-800 dark:text-zinc-200 p-3 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800"
          onClick={toggleAskMe}
        >
          <FaX />
        </button>
      </div>

      {/* Messages Container */}
      <div className="flex-1 scroll2 overflow-y-auto p-4 space-y-4">
        {!!messages && messages.map((message) => (
          <div
            key={`${message.role}-${message.timestamp}`}
            className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-md py-2 px-4 rounded-xl ${message.role === "user"
                ? "bg-blue-600 text-zinc-100"
                : "bg-zinc-200 dark:bg-zinc-700/50 text-zinc-900 dark:text-zinc-200 border border-gray-200 dark:border-gray-800/50"
                }`}
            >
              <div
                className="text-sm"
                dangerouslySetInnerHTML={{ __html: marked(message.text) }}
              />
            </div>
          </div>
        ))}
        {messages.length === 0 && (
          <div className="flex justify-center items-center h-full">
            <div className="max-w-md pt-4 pb-2 px-2 rounded-lg text-zinc-400">
              <p className="text-sm text-center">Ask Dhvanit is youe friendly AI assistant that can answer any question about me</p>
            </div>
          </div>
        )}
        {gettingResponse && <div className="flex justify-start">
          <div className="max-w-md pt-4 pb-2 px-2 rounded-lg bg-zinc-200 dark:bg-zinc-700/50 text-zinc-900 dark:text-zinc-200 border border-gray-200 dark:border-gray-800/50">
            <LoadingDots />
          </div>
        </div>}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="px-4 pt-4 pb-6 space-y-3.5 border-zinc-300 dark:border-zinc-800 bg-zinc-100/20 dark:bg-zinc-900/20 border-t text-zinc-900"
      >
        <div className="flex space-x-2 text-zinc-900 dark:text-zinc-200 text-sm overflow-x-auto no-scrollbar">
          {questionSuggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => setNewMessage(suggestion)}
              className="px-4 py-2 text-nowrap rounded-lg transition-all duration-200 hover:scale-95 bg-zinc-50 hover:bg-zinc-200 dark:bg-zinc-800/80 dark:hover:bg-zinc-700/80"
            >
              {suggestion}
            </button>
          ))}
        </div>
        <div className="flex rounded-full overflow-hidden">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter" && !gettingResponse) {
                handleSendMessage();
              }
            }}
            placeholder="Type a message..."
            className="flex-1 py-2 px-6 border rounded-s-full rounded-e-sm border-gray-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700 rounded-lg focus:outline-none focus:border-blue-500"
            disabled={gettingResponse}
          />
          <button
            onClick={handleSendMessage}
            className="pr-6 pl-5 py-2 rounded-s-sm rounded-e-full bg-blue-600 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-blue-300 dark:disabled:bg-blue-900 disabled:cursor-not-allowed"
            disabled={gettingResponse || !newMessage.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default AskMe;